// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query';
import { getWorkspacesApi } from './api';

export interface ApplicationInfo {
  appId: string;
  name: string;
  description: string | null;
}

export interface TemplateInfo {
  templateId: string;
  name: string;
  description: string | null;
}

export interface CreateFromApplicationRequest {
  name: string;
  templateId?: string;
  requestPrefix?: string;
}

export interface CreateFromApplicationResponse {
  orgId: string;
  wsId: string;
  name: string;
}

export const applicationKeys = {
  all: ['applications'] as const,
  list: () => [...applicationKeys.all, 'list'] as const,
  templates: (appId: string) => [...applicationKeys.all, appId, 'templates'] as const,
};

/** Shared query for the `/applications` catalog. */
export function useApplications(enabled = true) {
  const appApi = useWorkspaceWizardApi();
  return useQuery({
    queryKey: applicationKeys.list(),
    queryFn: () => appApi.listApplications(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export type WorkspaceWizardApi = ReturnType<typeof useWorkspaceWizardApi>;

export const useWorkspaceWizardApi = () => {
  const apiInstance = getWorkspacesApi();

  return {
    listApplications: async (): Promise<ApplicationInfo[]> => {
      console.log('[WorkspaceWizardApi] Fetching available applications');
      const response = await apiInstance.get<ApplicationInfo[]>('/applications');
      return response.data;
    },

    listTemplates: async (appId: string): Promise<TemplateInfo[]> => {
      console.log('[WorkspaceWizardApi] Fetching templates', { appId });
      const response = await apiInstance.get<TemplateInfo[]>(`/applications/${appId}/templates`);
      return response.data;
    },

    createFromApplication: async (
      orgId: string,
      appId: string,
      request: CreateFromApplicationRequest
    ): Promise<CreateFromApplicationResponse> => {
      console.log('[WorkspaceWizardApi] Creating workspace from application', { orgId, appId, request });
      const response = await apiInstance.post<CreateFromApplicationResponse>(
        `/applications/${appId}/org/${orgId}/workspaces`,
        request
      );
      return response.data;
    },
  };
};
